/**
 * Draft Order Checkout
 * Shared module to create a draft order via external API and redirect to checkout.
 */
const DraftOrderCheckout = (() => {
  const API_URL =
    "https://api.lens-labo.com/Api/Shopify/v1/CreateDraftOrder";
  const SHOP_CD = "31";

  /**
   * Build cartItems payload from cart page data.
   * Reads cart item rows from the DOM (both main cart and cart drawer).
   * @param {string} containerSelector - CSS selector for the cart items container
   * @returns {Array} cartItems array for the API
   */
  function collectCartItems(containerSelector) {
    const container = document.querySelector(containerSelector);
    if (!container) return [];

    const rows = container.querySelectorAll("tr.cart-item");
    const cartItems = [];

    rows.forEach((row) => {
      const variantId = row.dataset.variantId;
      const itemCD = row.dataset.itemCd || "";
      const quantity = parseInt(row.dataset.quantity, 10) || 1;
      const propertiesJson = row.dataset.properties;

      const customAttributes = [];
      if (propertiesJson) {
        try {
          const properties = JSON.parse(propertiesJson);
          if (Array.isArray(properties)) {
            // Shopify {{ item.properties | json }} outputs an array of arrays: [["key", "value"], ...]
            properties.forEach((prop) => {
              if (Array.isArray(prop) && prop.length >= 2) {
                const key = prop[0];
                const value = prop[1];
                if (key && value && !String(key).startsWith("_")) {
                  customAttributes.push({ key: String(key), value: String(value) });
                }
              }
            });
          } else if (properties && typeof properties === "object") {
            // Fallback for object format { "key": "value" }
            for (const [key, value] of Object.entries(properties)) {
              if (key && value && !String(key).startsWith("_")) {
                customAttributes.push({ key: String(key), value: String(value) });
              }
            }
          }
        } catch (e) {
          console.error("Failed to parse cart item properties:", e);
        }
      }

      if (variantId) {
        cartItems.push({
          variantId: String(variantId),
          quantity,
          itemCD,
          customAttributes,
        });
      }
    });

    return cartItems;
  }

  /**
   * Fetch cart items directly from Shopify API (fallback for cart-notification popup).
   */
  async function fetchCartItemsFromAPI() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      return cart.items.map(item => {
        const customAttributes = [];
        let itemCD = "";
        let setQty = "";
        
        if (item.properties) {
          for (const [key, value] of Object.entries(item.properties)) {
            if (key === '商品番号') {
              itemCD = value;
            }
            if (key && value && !String(key).startsWith("_")) {
               customAttributes.push({ key: String(key), value: String(value) });
            }
          }
        }
        
        // Find set from options if any (Shopify cart API format)
        if (item.options_with_values) {
           const setOpt = item.options_with_values.find(opt => opt.name === 'Set');
           if (setOpt && setOpt.value) setQty = '-' + setOpt.value;
        }

        return {
          variantId: String(item.variant_id),
          quantity: item.quantity || 1,
          itemCD: itemCD + setQty,
          customAttributes
        };
      });
    } catch (e) {
      console.error("Failed to fetch cart from API", e);
      return [];
    }
  }

  /**
   * Build cartItems payload for a single product (Buy it now).
   * @param {Object} opts
   * @param {string} opts.variantId
   * @param {number} opts.quantity
   * @param {string} opts.itemCD
   * @param {Array}  opts.customAttributes - [{key, value}, ...]
   * @returns {Array} cartItems array for the API
   */
  function buildSingleItem({ variantId, quantity, itemCD, customAttributes }) {
    return [
      {
        variantId: String(variantId),
        quantity: quantity || 1,
        itemCD: itemCD || "",
        customAttributes: customAttributes || [],
      },
    ];
  }

  /**
   * Call the draft order API and redirect to the checkout URL.
   * @param {Array} cartItems - array of cart item objects
   * @param {HTMLElement|null} button - optional button element to show loading state
   */
  async function createDraftOrderAndRedirect(cartItems, button) {
    if (!cartItems || cartItems.length === 0) {
      alert("カートにアイテムがありません。");
      return;
    }

    // Show loading state
    if (button) {
      button.setAttribute("aria-disabled", "true");
      button.classList.add("loading");
      const spinner = button.querySelector(".loading__spinner") || button.querySelector(".loading-overlay__spinner");
      if (spinner) {
        spinner.classList.remove("hidden");
        // Also hide label if standard shopify structure
        const label = button.querySelector(".hidden-on-loading");
        if (label) label.classList.add("hidden");
      } else {
        // Fallback: Store original html and show a basic loading text
        if (!button.dataset.originalHtml) {
          button.dataset.originalHtml = button.innerHTML;
          button.innerHTML = '処理中...'; // "Processing..."
        }
      }
    }

    const body = {
      shopCD: SHOP_CD,
      cartItems,
    };

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const rawText = await response.text();
      let data = {};
      try {
        data = JSON.parse(rawText);
      } catch (e) {
        // If it's not JSON, assume the raw text is the error message itself
        console.error("Failed to parse JSON response:", rawText);
        alert(rawText || "チェックアウトの作成に失敗しました。");
        return;
      }

      if (data.ISERROR === 0 && data.CHECKOUTURL) {
        window.location.href = data.CHECKOUTURL;
      } else {
        let messages = "チェックアウトの作成に失敗しました。";
        if (data.MESSAGES && Array.isArray(data.MESSAGES) && data.MESSAGES.length > 0) {
            messages = data.MESSAGES.join("\n");
        } else if (data.MESSAGES) {
            messages = String(data.MESSAGES);
        } else if (data.message) {
            messages = String(data.message);
        }
        alert(messages);
      }
    } catch (error) {
      console.error("Draft order creation failed:", error);
      alert(
        "チェックアウトの作成中にエラーが発生しました。もう一度お試しください。",
      );
    } finally {
      // Reset loading state
      if (button) {
        button.removeAttribute("aria-disabled");
        button.classList.remove("loading");
        const spinner = button.querySelector(".loading__spinner") || button.querySelector(".loading-overlay__spinner");
        if (spinner) {
          spinner.classList.add("hidden");
          const label = button.querySelector(".hidden-on-loading");
          if (label) label.classList.remove("hidden");
        } else if (button.dataset.originalHtml) {
          button.innerHTML = button.dataset.originalHtml;
          delete button.dataset.originalHtml;
        }
      }
    }
  }

  return {
    collectCartItems,
    buildSingleItem,
    createDraftOrderAndRedirect,
    fetchCartItemsFromAPI,
  };
})();

document.addEventListener("DOMContentLoaded", () => {
  document.body.addEventListener("click", async (event) => {
    const isBuyNow = event.target.closest('.shopify-payment-button__button') || 
                     event.target.closest('.shopify-payment-button__button--unbranded') ||
                     event.target.closest('.shopify-payment-button') ||
                     event.target.closest('shopify-payment-button');
                     
    if (isBuyNow) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const productForm = event.target.closest('form[action*="/cart/add"]');
      if (!productForm) return;

      const variantInput = productForm.querySelector('input[name="id"]');
      const qtyInput = productForm.querySelector('input[name="quantity"]') || { value: 1 };
      const itemCdInput = productForm.querySelector('input[name="properties[商品番号]"]');
      
      let itemCD = itemCdInput ? itemCdInput.value : '';

      const propertiesEles = document.querySelectorAll('[name^="properties["][required]');
      if(Array.from(propertiesEles).some(input => input.value === "")){
        alert(Array.from(propertiesEles).find(input => input.value === "").dataset.name+'を選択してください。');
        return;
      }
      
      // Determine Set value from custom options if any
      const rightOption = productForm.querySelector('custom-option[data-position="Right"]');
      const leftOption = productForm.querySelector('custom-option[data-position="Left"]');
      
      let setValue = '';
      if (rightOption && rightOption.dataset.qty) {
          setValue = '-' + rightOption.dataset.qty;
      } else if (leftOption && leftOption.dataset.qty) {
          setValue = '-' + leftOption.dataset.qty;
      }
      
      const customAttributes = [];
      const propertyInputs = productForm.querySelectorAll('[name^="properties["]');
      propertyInputs.forEach(input => {
        if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
        
        const match = input.name.match(/properties\[(.*?)\]/);
        if (match && match[1]) {
          const key = match[1];
          const value = input.value;
          if (key && value && !String(key).startsWith("_")) {
            customAttributes.push({ key: String(key), value: String(value) });
          }
        }
      });

      const cartItems = DraftOrderCheckout.buildSingleItem({
        variantId: variantInput ? variantInput.value : '',
        quantity: parseInt(qtyInput.value) || 1,
        itemCD: itemCD + setValue,
        customAttributes: customAttributes
      });

      const buttonToLoad = event.target.closest('button') || event.target;
      await DraftOrderCheckout.createDraftOrderAndRedirect(cartItems, buttonToLoad);
      return;
    }

    const button = event.target.closest('button[name="checkout"]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    let containerSelector = "#main-cart-items table.cart-items tbody";
    
    // Check if within cart drawer
    const drawer = button.closest("cart-drawer");
    if (drawer) {
      containerSelector = "cart-drawer-items table.cart-items tbody";
    }

    let cartItems = DraftOrderCheckout.collectCartItems(containerSelector);
    
    // Fallback: If no items found in DOM (e.g. cart notification popup), fetch from API
    if (!cartItems || cartItems.length === 0) {
      cartItems = await DraftOrderCheckout.fetchCartItemsFromAPI();
    }
    
    await DraftOrderCheckout.createDraftOrderAndRedirect(cartItems, button);
  }, { capture: true });
});
